import { useState } from 'react';
import styled from 'styled-components';
import { Button } from '@shared/ui/Button';
import { IconCheck } from '@shared/ui/icons';
import { useAnnotateRiskMutation, useClearRiskAnnotationMutation } from '@shared/api';
import type { ContractRisk, RiskDecision } from '@entities/legal/types';
import { useAgentPermission } from '@entities/user';

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-top: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;
`;

const Pill = styled.button<{
  $tone: 'good' | 'bad' | 'neutral';
  $active: boolean;
}>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: ${({ theme }) => theme.radius.pill};
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  cursor: pointer;
  transition:
    background-color 160ms ease,
    color 160ms ease,
    border-color 160ms ease;
  border: 1px solid
    ${({ $tone, $active, theme }) =>
      $active
        ? $tone === 'good'
          ? theme.colors.status.success
          : $tone === 'bad'
            ? theme.colors.status.danger
            : theme.colors.status.warning
        : theme.colors.border};
  background: ${({ $tone, $active, theme }) =>
    $active
      ? $tone === 'good'
        ? theme.colors.status.success + '22'
        : $tone === 'bad'
          ? theme.colors.status.danger + '22'
          : theme.colors.status.warning + '22'
      : 'transparent'};
  color: ${({ $tone, $active, theme }) =>
    $active
      ? $tone === 'good'
        ? theme.colors.status.success
        : $tone === 'bad'
          ? theme.colors.status.danger
          : theme.colors.status.warning
      : theme.colors.text.muted};

  &:hover {
    background: ${({ theme }) => theme.colors.surface};
  }
`;

const CommentBox = styled.textarea`
  width: 100%;
  min-height: 50px;
  padding: 6px 10px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text.primary};
  font: inherit;
  font-size: 12px;
  line-height: 1.4;
  resize: vertical;
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

const StatusLine = styled.div`
  margin-top: ${({ theme }) => theme.spacing(2)};
  margin-left: 8px;
  padding-left: 10px;
  border-left: 2px solid ${({ theme }) => theme.colors.border};
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const Meta = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const DECISION_LABELS: Record<RiskDecision, string> = {
  accepted: 'Принять',
  disputed: 'Под вопросом',
  rejected: 'Отклонить',
};

const DECISION_DONE: Record<RiskDecision, string> = {
  accepted: 'Принят юристом',
  disputed: 'Под вопросом',
  rejected: 'Отклонён юристом',
};

const DECISION_TONE: Record<RiskDecision, 'good' | 'bad' | 'neutral'> = {
  accepted: 'good',
  disputed: 'neutral',
  rejected: 'bad',
};

interface Props {
  risk: ContractRisk;
  contractId: string;
}

export function RiskAnnotation({ risk, contractId }: Props) {
  const { can_write } = useAgentPermission('statut');
  const [annotate] = useAnnotateRiskMutation();
  const [clearAnnotation] = useClearRiskAnnotationMutation();
  const [commentDraft, setCommentDraft] = useState<string>(risk.decision_comment ?? '');
  const [editingComment, setEditingComment] = useState(false);

  const decision = (risk.decision as RiskDecision | null) ?? null;

  // Кнопки никогда не disabled: оптимистичный апдейт в RTK Query
  // (см. onQueryStarted в useAnnotateRiskMutation) уже мгновенно патчит
  // кэш карточки, поэтому юрист видит изменение состояния сразу. Если
  // сервер вернёт ошибку — patch.undo() откатит. Это даёт мгновенный
  // отклик UI вне зависимости от сетевой задержки.
  function pick(d: RiskDecision) {
    void annotate({
      contractId,
      riskId: risk.id,
      decision: d,
      comment: commentDraft.trim() || undefined,
    });
    setEditingComment(false);
  }

  function clear() {
    void clearAnnotation({ contractId, riskId: risk.id });
    setCommentDraft('');
    setEditingComment(false);
  }

  return (
    <>
      <Row>
        {(['accepted', 'disputed', 'rejected'] as RiskDecision[]).map((d) => (
          <Pill
            key={d}
            type="button"
            $tone={DECISION_TONE[d]}
            $active={decision === d}
            onClick={() => pick(d)}
            disabled={!can_write}
            title={!can_write ? 'Только чтение' : undefined}
          >
            {decision === d && <IconCheck size={12} />}
            {DECISION_LABELS[d]}
          </Pill>
        ))}
        {decision && (
          <Button
            type="button"
            $size="sm"
            $variant="secondary"
            onClick={clear}
            disabled={!can_write}
            title={!can_write ? 'Только чтение' : undefined}
          >
            Сбросить
          </Button>
        )}
        <Button
          type="button"
          $size="sm"
          $variant="secondary"
          onClick={() => setEditingComment((v) => !v)}
          disabled={!can_write}
          title={!can_write ? 'Только чтение' : undefined}
        >
          {editingComment ? 'Скрыть комментарий' : '+ Комментарий'}
        </Button>
      </Row>

      {editingComment && (
        <CommentBox
          value={commentDraft}
          onChange={(e) => setCommentDraft(e.target.value)}
          placeholder="Комментарий юриста к решению (опционально). Будет виден в audit-log и в карточке."
        />
      )}

      {decision && (
        <StatusLine>
          <strong>{DECISION_DONE[decision]}</strong>
          {risk.decision_at && (
            <>
              {' · '}
              <Meta>{new Date(risk.decision_at).toLocaleString('ru-RU')}</Meta>
            </>
          )}
          {risk.decision_comment && (
            <>
              {' · '}
              <span>«{risk.decision_comment}»</span>
            </>
          )}
        </StatusLine>
      )}
    </>
  );
}
